/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha, useTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import { Box } from '@mui/system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import type {
  Coverage,
  GridDomain,
  PointSeriesDomain,
} from '@opengeoweb/shared';
import { layerActions } from '@opengeoweb/store';
import FlySafeLegend from './FlySafeLegend';
import FlySafeLocation from './FlySafeLocation';
import ShareButton from './ShareButton';
import {
  getCachedStationCoverageById,
  getCachedStationCoverageForPosition,
  getCachedSZACoverageById,
  getCachedSZACoverageForPosition,
} from '../edr-api/cachedApi';
import type { ShareParams } from '../utils/shareParams';
import { DAY_MS, floorToInterval, MIN_MS, WEEK_MS } from '../utils/timeUtils';
import type { Mode } from '../utils/types';

export interface DataRequest {
  lon: number;
  lat: number;
}

export interface DataIdRequest {
  id: string;
}
interface Props {
  initialMode?: Mode;
  animationWindow?: [Date, Date] | null;
  onAnimationWindowChange?: (window: [Date, Date]) => void;
  dataRequest?: DataRequest | null;
  defaultLayerId: string;
  mapTime?: Date | null;
  onTimeSelect?: (time: Date) => void;
  forceRefetchState: boolean;
  shareParams?: ShareParams | null;
  onSelectedStationsChange?: (ids: string[]) => void;
}

// All the info existing per location
interface CardMeta {
  stationId?: string;
  coverage?: Coverage<GridDomain>;
  szaCoverage?: Coverage<PointSeriesDomain>;
}

interface PanelState {
  items: number[];
  nextId: number;
  mode: Mode;
  timeSpan: 'day' | 'week';
  intervalMinutes: number;
  byIdMeta: Record<number, CardMeta>;
}

const buildInitialState = (initialMode?: Mode): PanelState => {
  const intervalMinutes = 5;

  return {
    items: [],
    nextId: 1,
    mode: (initialMode ?? 'birdtam') as Mode,
    timeSpan: 'day',
    intervalMinutes,
    byIdMeta: {},
  };
};

const FlySafePanel = ({
  initialMode,
  animationWindow,
  onAnimationWindowChange,
  dataRequest,
  defaultLayerId,
  mapTime,
  onTimeSelect,
  forceRefetchState,
  shareParams,
  onSelectedStationsChange,
}: Props): ReactNode => {
  const theme = useTheme();
  const [panel, setPanel] = useState<PanelState>(() => {
    const base = buildInitialState(initialMode);
    if (shareParams) {
      return {
        ...base,
        mode: shareParams.mode ?? base.mode,
        timeSpan: shareParams.timeSpan ?? base.timeSpan,
      };
    }
    return base;
  });
  const [showVPTS, setShowVPTS] = useState(shareParams?.showVPTS ?? true);
  const [showVID, setShowVID] = useState(shareParams?.showVID ?? true);

  const removeLocation = useCallback((id: number) => {
    setPanel((state) => {
      const { [id]: drop, ...rest } = state.byIdMeta;
      return {
        ...state,
        items: state.items.filter((x) => x !== id),
        byIdMeta: rest,
      };
    });
  }, []);

  const handleModeSwitch = useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setPanel((state) => ({
        ...state,
        mode: checked ? 'birdtam' : 'density',
      }));
    },
    [],
  );

  const handleTimeSpanSwitch = useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const nextSpan = checked ? 'week' : 'day';
      setPanel((state) => ({
        ...state,
        timeSpan: nextSpan,
      }));

      if (onAnimationWindowChange) {
        const spanMs = nextSpan === 'week' ? WEEK_MS : DAY_MS;
        const step = panel.intervalMinutes * MIN_MS;
        const endMs = floorToInterval(
          animationWindow ? animationWindow[1].getTime() : Date.now(),
          step,
        );
        const startMs = floorToInterval(endMs - spanMs, step);
        onAnimationWindowChange([new Date(startMs), new Date(endMs)]);
      }
    },
    [onAnimationWindowChange, animationWindow, panel.intervalMinutes],
  );

  const handleVPTSChange = useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setShowVPTS(checked);
    },
    [],
  );

  const handleVIDChange = useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setShowVID(checked);
    },
    [],
  );

  const upsertCoverage = useCallback(
    (
      stationId: string,
      cov?: Coverage<GridDomain>,
      szaCov?: Coverage<PointSeriesDomain>,
    ) => {
      if (!stationId) {
        return;
      }

      setPanel((state) => {
        const existing = state.items.find(
          (id) => state.byIdMeta[id]?.stationId === stationId,
        );
        if (existing) {
          // Only overwrite slots for which we have new data so a failed
          // refetch does not wipe previously fetched data for this card.
          const prev = state.byIdMeta[existing];
          return {
            ...state,
            byIdMeta: {
              ...state.byIdMeta,
              [existing]: {
                ...prev,
                coverage: cov ?? prev.coverage,
                szaCoverage: szaCov ?? prev.szaCoverage,
              },
            },
          };
        }

        // Do not create a new card unless we have at least the station
        // coverage; without it there is nothing to plot.
        if (!cov) {
          return state;
        }

        const newId = state.nextId;
        return {
          ...state,
          items: [...state.items, newId],
          nextId: newId + 1,
          byIdMeta: {
            ...state.byIdMeta,
            [newId]: {
              stationId,
              coverage: cov,
              szaCoverage: szaCov ?? undefined,
            },
          },
        };
      });
    },
    [],
  );

  const fetchAndUpsertCoverage = useCallback(
    async (
      req: DataRequest,
      minTime: number,
      maxTime: number,
      baseDate?: Date,
    ) => {
      const days = Math.max(1, Math.ceil((maxTime - minTime) / DAY_MS));

      // Fetch station coverage and SZA independently so one failing does
      // not prevent the other from being displayed.
      const [covResult, szaResult] = await Promise.allSettled([
        getCachedStationCoverageForPosition([req.lon, req.lat], days, baseDate),
        getCachedSZACoverageForPosition([req.lon, req.lat], days, baseDate),
      ]);

      if (covResult.status === 'rejected') {
        // Without station coverage we have no stationId in this
        // position-based flow, so we cannot attach the SZA data to a
        // card either. Log both and bail.
        console.error('EDR position error (coverage):', covResult.reason);
        if (szaResult.status === 'rejected') {
          console.error('EDR position error (SZA):', szaResult.reason);
        }
        return;
      }

      const cov = covResult.value;
      const covWithId = cov as Coverage<GridDomain> & { id?: string };
      const stationId = covWithId.id ? String(covWithId.id) : '';
      if (!stationId) {
        console.error(
          'EDR position error: coverage returned without station id',
        );
        return;
      }

      if (szaResult.status === 'rejected') {
        console.error('EDR position error (SZA):', szaResult.reason);
      }

      upsertCoverage(
        stationId,
        cov,
        szaResult.status === 'fulfilled' ? szaResult.value : undefined,
      );
    },
    [upsertCoverage],
  );

  const fetchAndUpsertCoverageById = useCallback(
    async (
      req: DataIdRequest,
      minTime: number,
      maxTime: number,
      baseDate: Date,
    ) => {
      const days = Math.max(1, Math.ceil((maxTime - minTime) / DAY_MS));

      // Fetch station coverage and SZA independently so one failing does
      // not prevent the other from being displayed.
      const [covResult, szaResult] = await Promise.allSettled([
        getCachedStationCoverageById(req.id, days, baseDate),
        getCachedSZACoverageById(req.id, days, baseDate),
      ]);

      if (covResult.status === 'rejected') {
        console.error('EDR position error (coverage):', covResult.reason);
      }
      if (szaResult.status === 'rejected') {
        console.error('EDR position error (SZA):', szaResult.reason);
      }

      upsertCoverage(
        req.id,
        covResult.status === 'fulfilled' ? covResult.value : undefined,
        szaResult.status === 'fulfilled' ? szaResult.value : undefined,
      );
    },
    [upsertCoverage],
  );
  // Restore stations from share params on mount
  useEffect(() => {
    if (
      !shareParams?.stationIds ||
      shareParams.stationIds.length === 0 ||
      !shareParams?.animationEnd
    ) {
      return;
    }
    const step = panel.intervalMinutes * MIN_MS;
    const endMs = floorToInterval(
      new Date(shareParams.animationEnd).valueOf(),
      step,
    );
    const weekAgo = floorToInterval(endMs - WEEK_MS - DAY_MS, step);
    const baseDate = new Date(endMs);

    shareParams.stationIds.forEach((id) => {
      void fetchAndUpsertCoverageById(
        { id },
        weekAgo,
        baseDate.valueOf(),
        baseDate,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { items, mode, timeSpan, intervalMinutes, byIdMeta } = panel;

  const stationIds = useMemo(
    () =>
      items
        .map((id) => byIdMeta[id]?.stationId)
        .filter((sid): sid is string => !!sid),
    [items, byIdMeta],
  );

  useEffect(() => {
    onSelectedStationsChange?.(stationIds);
  }, [stationIds, onSelectedStationsChange]);

  // Day-boundary debounce: only changes when animation end crosses a day
  const animationEndDay = useMemo(
    () =>
      animationWindow
        ? Math.floor(animationWindow[1].getTime() / DAY_MS)
        : null,
    [animationWindow],
  );

  // Fetch a full week of data when a new station is clicked
  // or when the animation window end crosses a day boundary
  useEffect(() => {
    if (!dataRequest) {
      return;
    }

    const step = panel.intervalMinutes * MIN_MS;
    const endTime = animationWindow ? animationWindow[1].getTime() : Date.now();
    const end = floorToInterval(endTime, step);
    const weekAgo = floorToInterval(end - WEEK_MS - DAY_MS, step);
    void fetchAndUpsertCoverage(dataRequest, weekAgo, end, new Date(end));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataRequest, panel.intervalMinutes, fetchAndUpsertCoverage]);

  // Refetch coverages when necessary
  useEffect(() => {
    Object.values(panel.byIdMeta).forEach((cm) => {
      if (!cm?.stationId) {
        return;
      }
      const step = panel.intervalMinutes * MIN_MS;
      const endTime = animationWindow
        ? animationWindow[1].getTime()
        : Date.now();
      const end = floorToInterval(endTime, step);
      const weekAgo = floorToInterval(end - WEEK_MS - DAY_MS, step);

      void fetchAndUpsertCoverageById(
        {
          id: cm.stationId,
        },
        weekAgo,
        end,
        new Date(end),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    panel.intervalMinutes,
    fetchAndUpsertCoverage,
    animationEndDay,
    forceRefetchState,
  ]);

  const stepMs = intervalMinutes * MIN_MS;

  const dispatch = useDispatch();

  useEffect(() => {
    if (defaultLayerId) {
      dispatch(
        layerActions.layerChangeName({
          layerId: defaultLayerId,
          name: mode === 'density' ? 'vid' : mode,
        }),
      );
    }
  }, [mode, defaultLayerId, dispatch]);

  // Use animation window from the map time slider, fall back to day/week window
  const memoizedTimeWindow = useMemo<[Date, Date]>(() => {
    if (animationWindow) {
      return animationWindow;
    }
    const spanMs = timeSpan === 'week' ? WEEK_MS : DAY_MS;
    const now = floorToInterval(Date.now(), stepMs);
    return [new Date(floorToInterval(now - spanMs, stepMs)), new Date(now)];
  }, [animationWindow, stepMs, timeSpan]);

  return (
    <div>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.tooltip + 10,
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: 'blur(4px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          // eslint-disable-next-line id-length
          p: '10px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareButton
              animationWindow={animationWindow}
              mode={mode}
              showVID={showVID}
              showVPTS={showVPTS}
              stationIds={stationIds}
              timeSpan={timeSpan}
            />
          </Box>

          <Box
            sx={{
              display: 'block',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showVPTS}
                  onChange={handleVPTSChange}
                  size="small"
                />
              }
              label="VPTS"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showVID}
                  onChange={handleVIDChange}
                  size="small"
                />
              }
              label="VID"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={mode === 'birdtam'}
                  color="primary"
                  onChange={handleModeSwitch}
                />
              }
              label={mode === 'birdtam' ? 'BirdTAM' : 'Density'}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={timeSpan === 'week'}
                  color="primary"
                  onChange={handleTimeSpanSwitch}
                />
              }
              label={timeSpan === 'week' ? 'Week' : 'Day'}
            />
          </Box>
        </Box>
        <Box sx={{ width: '100%', flex: 1 }}>
          <FlySafeLegend mode={mode} />
        </Box>
      </Box>
      <div>
        {items.map((id) => (
          // eslint-disable-next-line id-length
          <Box key={id} sx={{ p: 1.5 }}>
            <FlySafeLocation
              coverage={byIdMeta[id]?.coverage}
              id={id}
              mapTime={mapTime}
              mode={mode}
              onClose={removeLocation}
              onTimeSelect={onTimeSelect}
              showVID={showVID}
              showVPTS={showVPTS}
              stationId={byIdMeta[id]?.stationId}
              szaCoverage={byIdMeta[id]?.szaCoverage}
              timeWindow={memoizedTimeWindow}
            />
          </Box>
        ))}
      </div>
      {/* eslint-disable i18next/no-literal-string */}
      <div className="markdown-content">
        <h1>Real-time animation and altitude profiles of bird densities</h1>

        <p>
          This page provides the 5-minute real-time bird density composite
          measured by operational weather radars in Germany, the Netherlands and
          Belgium. The density of biomass is expressed as vertically integrated
          bird densities per km2 or as BIRDTAM values used in aviation (the
          radio button switches between options). BIRDTAM values follow a
          logarithmic scale from 0-8. Rain is not filtered and can be easily
          recognized as large, sharp shaped, blue-black areas. For more
          information on how these profiles are integrated, see Kranstauber et
          al.,{' '}
          <a
            href="https://doi.org/10.3390/rs12040635"
            rel="noreferrer"
            target="_blank"
          >
            2020
          </a>
        </p>

        <p>
          Click on one of the radar locations in the animation to get a panel
          with altitude profiles of bird density derived from that radar.
          Altitude is provided above mean sea level, wind barbs provide mean
          bird speed and direction into which the birds are flying. The
          vertically integrated bird density is given in a second graph, with
          birds per km2 on the left Y-axis and BIRDTAM values on the right
          Y-axis. The radio button switches between density or BIRDTAM colors.
        </p>
      </div>
      {/* eslint-enable i18next/no-literal-string */}
    </div>
  );
};

export default FlySafePanel;
